import { Notation } from '@alucio/aws-beacon-amplify/src/models'
import { NOTATION_TYPE } from '@alucio/aws-beacon-amplify/src/API';
import {
  PresentationContentWithNotations,
  PresentationContentState,
} from './state/context/PresentationPlayer/types'

// REACT TYPES
export type DisplayNameChild<T extends React.ReactElement> = T & {
  type: { displayName?: string }
}

export enum BeaconEvents {
  presentationHeartbeat,
  attendeeReady,
  attendeeIframeReady,
  recordingStatus,
  shareScreen,
  stopAllShareScreen
}

// We assign hard coded numbers here to maintain backwards
// compatability with existing data which used these numbers
export enum PlayerActions {
  next = 0,
  prev = 1,
  gotoSlide = 2,
  jumpToAnim = 10,
  animateTransform = 30,
  authCloudFront = 27,
}

export enum DatatrackDataTypes {
  BeaconControl,
  BeaconEvent,
  PlayerControl,
  PlayerEvent,
  Chat,
}

export class DatatrackData {
  constructor() {
    this.timestamp = new Date()
  }

  type?: DatatrackDataTypes | undefined
  timestamp: Date
}
export class ControlData extends DatatrackData {
  constructor(action, param) {
    super()
    this.action = action
    this.param = param
  }

  action: BeaconActions | PlayerActions
  param: any
}

export enum BeaconActions {
  startPresentation,
  startScreenShare,
  stopPresentation
}

export class BeaconControlData extends ControlData {
  constructor(action: BeaconActions, param?) {
    super(action, param)
    this.type = DatatrackDataTypes.BeaconControl
  }
}

export class PlayerControlData extends ControlData {
  constructor(action: PlayerActions, param?) {
    super(action, param)
    this.type = DatatrackDataTypes.PlayerControl
  }
}

export enum PlayerEvents {
  onLoadingStart,
  onLoadingEnd,
  onSlideChange,
  onMovToNxtAnim,
  onMovToPrvAnim,
  onPlayingStart,
  onPlayingEnd,
  onProgressChange,
  onSlideEnd,
  onSldOutlineLoad,
  initaliseSettings,
  mouseEvent,
  statusEvent,
  swipeVertical,
}

class EventData extends DatatrackData {
  constructor(event, param?) {
    super()
    this.event = event
    this.param = param
  }

  event: PlayerEvents | BeaconEvents
  param: any
}
export class PlayerEventData extends EventData {
  constructor(event: PlayerEvents, param?) {
    super(event, param)
    this.type = DatatrackDataTypes.PlayerEvent
  }
}

enum PlayerModes {
  'INTERACTIVE',
  'NON_INTERACTIVE',
}
export type PlayerMode = keyof typeof PlayerModes

enum HubDeviceModes {
  'desktop' = 'desktop',
  'tablet' = 'tablet',
  'mobile' = 'mobile',
}
export type HubDeviceMode = keyof typeof HubDeviceModes

export type PlayerModeMessage = {
  mode: PlayerMode
  hubDeviceMode?: HubDeviceMode,
}

// PPZ is an acronym for Pan Pinch Zoom
export type PPZTransform = {
  positionX: number,
  positionY: number,
  scale: number,
}

export type SetPresentationMeta = {
  groupId?: string,
  customDeckId?: string,
  visiblePages?: number[],
  hiddenPages?: number[],
  slide?: number,
  step?: number,
}

export interface Coordinates {
  x: number,
  y: number,
}

export interface Dimensions {
  height: number,
  width: number,
}

export type SetPageAndPPZTransform = {
  positionX: number,
  positionY: number,
  scale: number,
  page: number,
}

export enum PDFMessageTypes {
  LOAD_FILE = 'LOAD_FILE',
  IFRAME_LOADED = 'IFRAME_LOADED',
  PDF_PAGE_LOADED = 'PDF_PAGE_LOADED',
  PLAYER_MODE = 'PLAYER_MODE',
  PLAYER_CONTROL = 'PLAYER_CONTROL',
  PPZ_TRANSFORM = 'PPZ_TRANSFORM',
  SET_PRESENTATION_STATE = 'SET_PRESENTATION_STATE',
  SET_VISIBLE_PAGES = 'SET_VISIBLE_PAGES',
  SET_PRESENTATION_META = 'SET_PRESENTATION_META',
  PAGE_CHANGE = 'PAGE_CHANGE',
  SET_CONTENT_ACCESS_TOKEN = 'SET_CONTENT_ACCESS_TOKEN',
  DOCUMENT_LOADED = 'DOCUMENT_LOADED',
  NAVIGATE_PREVIOUS = 'NAVIGATE_PREVIOUS',
  NAVIGATE_NEXT = 'NAVIGATE_NEXT',
  NAVIGATE_PAST_FIRST = 'NAVIGATE_PAST_FIRST',
  NAVIGATE_PAST_LAST = 'NAVIGATE_PAST_LAST',
  PAUSE_VIDEOS = 'PAUSE_VIDEOS',
  SWIPE_VERTICAL = 'SWIPE_VERTICAL',
  SHOW_SEARCH = 'SHOW_SEARCH',
  SHOW_MY_CONTENT = 'SHOW_MY_CONTENT',
  CURRENT_CONTENT_INFO = 'CURRENT_CONTENT_INFO',
  TOGGLE_HIGHLIGHTER = 'TOGGLE_HIGHLIGHTER',
  TOGGLE_NOTATIONS = 'TOGGLE_NOTATIONS',
  TOGGLE_COORDINATE_MODE = 'TOGGLE_COORDINATE_MODE',
  SEND_NOTATION_COORDINATE = 'SEND_NOTATION_COORDINATE',
  UPDATE_NOTATIONS = 'UPDATE_NOTATIONS',
  SET_EDIT_NOTATION = 'SET_EDIT_NOTATION',
  SYNC_CURRENT_ACTIVE_NOTATION = 'SYNC_CURRENT_ACTIVE_NOTATION',
  ANALYTICS = 'ANALYTICS',
  RELOAD_IFRAME = 'RELOAD_IFRAME',
  SET_SEARCH_HIGHLIGHT = 'SET_SEARCH_HIGHLIGHT',
}

export type VideoAnalyticsPayload = {
  documentId: string,
  documentVersionId: string,
  videoStartTime: number,
  videoEndTime: number,
}

export type NotationCoordinateData = {
  coordinate?: { x: number, y: number }
  notationId?: string
  pageId: string
}

export type Notations = {
  notations: Notation[]
}

export type ToggleNotationsData = {
  notationType?: NOTATION_TYPE
  value: boolean
}

// [TODO-915] - Make value tied to type
export type PDFChannelMessage = {
  type: keyof typeof PDFMessageTypes | PDFMessageTypes,
  value: string |
  number |
  number[] |
  boolean |
  PresentationContentWithNotations |
  PlayerControlData |
  SetPresentationMeta |
  PlayerModeMessage |
  PPZTransform |
  Dimensions |
  PresentationContentState |
  VideoAnalyticsPayload |
  NotationCoordinateData |
  ToggleNotationsData |
  Notations,
  frameId: string
}

export type ContentAuthHeaders = {
  'Alucio-Authorization': string,
  'Alucio-Content-Bucket': string,
}

export enum PlayerEventSource {
  APP = 'APP',
  PUBLISHER = 'PUBLISHER',
  MEETING = 'MEETING',
}

// hooks
export { default as useOnlineStatus } from './hooks/useOnlineStatus/useOnlineStatus'
export { default as useInterval } from './hooks/useInterval/useInterval'
export {
  default as usePresentationPlayerContext,
} from './hooks/usePresentationPlayerContext/usePresentationPlayerContext'

export * from './types/cacheDB'
export * from './types/workerChannel'
export * from './types/auth'

export {
  PresentationContext,
  usePresentationContext,
} from './state/context/PresentationPlayer/PresentationContext'

export {
  default as PresentationPlayerProvider,
} from './state/context/PresentationPlayerContext/PresentationPlayerContext'

export type {
  PresentationContentState,
  PresentationContent,
  PresentationContentWithNotations,
} from './state/context/PresentationPlayer/types'

export { PRESENTATION_CONTEXT } from './state/context/PresentationPlayer/types'

// util
export { reachabilityMonitor } from './hooks/useOnlineStatus/useOnlineStatus'
export { default as WebLinking } from './util/WebLinking'
export { nonSelectableElementHook } from './hooks/css/css'
export * from './util/Regex'

export * from './types/Platform'
